import { useQuery } from '@tanstack/react-query';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getKpiAccumulatedLatestValue,
  getKpiTimelineAggregatedValues,
} from '@/features/charts/api/get-charts-for-project';
import { ChartFromKpi } from '@/features/charts/components/chart-from-kpi';
import {
  AggregationType,
  ChartType,
} from '@/features/charts/components/create-chart-type-selection';
import { useChartDataForKpi } from '@/features/charts/hooks/use-chart-configs-for-project';
import { KpiAnomaly } from '@/features/insights/api/detect-kpi-anomalies';
import { TimelineChartContainer } from '@/features/insights/components/timeline-chart-container';
import { useInsightsHubChartsStore } from '@/features/insights/store/use-insights-hub-filter-store';
import { getKpiUnitTranslationPlural } from '@/shared/hooks/use-kpi-unit-translation';
import { KpiTrackingEntry, KpiWithMetrics } from '@/shared/types/api';

export const ChartsRow = ({
  kpi,
  projectId,
  isSubprojectFiltered,
  anomalies,
  fromDate,
  toDate,
}: {
  kpi?: KpiWithMetrics;
  projectId: number | undefined;
  isSubprojectFiltered: boolean;
  anomalies?: KpiAnomaly[] | undefined;
  fromDate?: Date;
  toDate?: Date;
}) => {
  if (!kpi) return null;

  const {
    timelineChartType,
    accumulatedChartType,
    initializeChartTypes,
    setTimelineChartType,
    setAccumulatedChartType,
  } = useInsightsHubChartsStore();

  useEffect(() => {
    if (kpi) {
      initializeChartTypes(kpi);
    }
  }, [kpi, initializeChartTypes]);

  return (
    <div className="flex flex-col gap-6 lg:flex-row">
      <div className="w-full">
        <TimelineChart
          kpi={kpi}
          projectId={projectId}
          isSubprojectFiltered={isSubprojectFiltered}
          anomalies={anomalies}
          chartType={timelineChartType}
          updateChartType={setTimelineChartType}
          fromDate={fromDate}
          toDate={toDate}
        />
      </div>
      <div className="w-full md:w-auto md:min-w-[350px]">
        <AccumulatedChart
          kpi={kpi}
          projectId={projectId}
          isSubprojectFiltered={isSubprojectFiltered}
          chartType={accumulatedChartType}
          updateChartType={setAccumulatedChartType}
          fromDate={fromDate}
          toDate={toDate}
        />
      </div>
    </div>
  );
};

const TimelineChart = ({
  kpi,
  projectId,
  isSubprojectFiltered,
  anomalies,
  chartType,
  updateChartType,
  fromDate,
  toDate,
}: {
  kpi?: KpiWithMetrics;
  projectId: number | undefined;
  isSubprojectFiltered: boolean;
  anomalies?: KpiAnomaly[] | undefined;
  chartType: ChartType;
  updateChartType: (chartType: ChartType) => void;
  fromDate?: Date;
  toDate?: Date;
}) => {
  if (!kpi) return <TimelineChartContainer />;

  const { t: tKpiTypes } = useTranslation('kpiTypes', { nsMode: 'fallback' });

  const { timelineChartTypes } = useInsightsHubChartsStore();

  // Determine if we should use aggregated timeline data
  const isTimelineChartType =
    chartType === ChartType.LINE || chartType === ChartType.BAR;
  const shouldAggregate =
    !isSubprojectFiltered && isTimelineChartType && !!projectId;

  const projectIdForEntries = shouldAggregate ? undefined : projectId;

  // Fetch aggregated timeline data for main project view
  const {
    data: aggregatedEntries,
    isLoading: isAggregatedLoading,
    isError: isAggregatedError,
  } = useQuery({
    queryKey: [
      'kpiTimelineAggregatedValues',
      projectId,
      kpi.id,
      'monthly',
      fromDate?.toISOString(),
      toDate?.toISOString(),
    ],
    queryFn: () =>
      getKpiTimelineAggregatedValues({
        projectId: projectId!,
        kpiId: kpi.id,
        period: 'monthly',
        fromDate,
        toDate,
      }),
    enabled: shouldAggregate,
  });

  const {
    data: kpiWithData,
    isLoading,
    isError,
  } = useChartDataForKpi({
    id: kpi.id,
    projectId: projectIdForEntries,
    monthsBack: fromDate || toDate ? undefined : 9,
    kpi,
    fromDate,
    toDate,
  });

  // Helper function to filter entries by date range (inclusive)
  const filterEntriesByDateRange = (
    entries: KpiTrackingEntry[],
  ): KpiTrackingEntry[] => {
    if (!fromDate && !toDate) return entries;

    return entries.filter((entry) => {
      if (!entry.created_at) return false;
      const entryDate = new Date(entry.created_at);

      // For fromDate: compare against start of day (00:00:00.000) - inclusive
      if (fromDate) {
        const fromDateStart = new Date(fromDate);
        fromDateStart.setHours(0, 0, 0, 0);
        if (entryDate < fromDateStart) return false;
      }

      // For toDate: compare against end of day (23:59:59.999) - inclusive
      if (toDate) {
        const toDateEnd = new Date(toDate);
        toDateEnd.setHours(23, 59, 59, 999);
        if (entryDate > toDateEnd) return false;
      }

      return true;
    });
  };

  // Handle aggregated timeline rendering
  if (shouldAggregate) {
    if (isAggregatedLoading || isAggregatedError || !aggregatedEntries)
      return <TimelineChartContainer />;

    // Check if we have data from multiple subprojects
    const hasMultipleSubprojects = aggregatedEntries.some(
      (entry) => entry.subprojectCount > 1,
    );

    // If only single subproject data, fall back to regular rendering
    if (!hasMultipleSubprojects && kpiWithData) {
      // Apply client-side date filtering as a safeguard
      const filteredEntries = filterEntriesByDateRange(
        kpiWithData.entries ?? [],
      );

      return (
        <TimelineChartContainer>
          <ChartFromKpi
            chartType={chartType}
            title={kpi.name}
            description={getKpiUnitTranslationPlural(tKpiTypes, kpi.unit)}
            kpi={{ ...kpiWithData, entries: filteredEntries }}
            aggregationType={AggregationType.TIMELINE}
            chartClassName="h-[225px]"
            onUpdateChartType={updateChartType}
            availableChartTypes={timelineChartTypes}
            unit={kpi.unit}
            anomalyData={anomalies}
          />
        </TimelineChartContainer>
      );
    }

    // Filter aggregated entries by date range (inclusive) and convert to synthetic KpiTrackingEntry format
    const filteredAggregatedEntries = aggregatedEntries.filter((entry) => {
      if (!fromDate && !toDate) return true;
      const entryDate = new Date(entry.periodStart);

      // For fromDate: compare against start of day (00:00:00.000) - inclusive
      if (fromDate) {
        const fromDateStart = new Date(fromDate);
        fromDateStart.setHours(0, 0, 0, 0);
        if (entryDate < fromDateStart) return false;
      }

      // For toDate: compare against end of day (23:59:59.999) - inclusive
      if (toDate) {
        const toDateEnd = new Date(toDate);
        toDateEnd.setHours(23, 59, 59, 999);
        if (entryDate > toDateEnd) return false;
      }

      return true;
    });

    const syntheticEntries: KpiTrackingEntry[] = filteredAggregatedEntries.map(
      (entry, index) => ({
        id: index,
        kpi_id: kpi.id,
        project_id: projectId!,
        report_id: 0,
        value: entry.aggregatedValue,
        created_at: entry.periodStart,
        updated_at: null,
        created_by: null,
        updated_by: null,
      }),
    );

    return (
      <TimelineChartContainer>
        <ChartFromKpi
          chartType={chartType}
          title={kpi.name}
          description={getKpiUnitTranslationPlural(tKpiTypes, kpi.unit)}
          kpi={{ ...kpi, entries: syntheticEntries }}
          aggregationType={AggregationType.TIMELINE}
          chartClassName="h-[225px]"
          onUpdateChartType={updateChartType}
          availableChartTypes={timelineChartTypes}
          unit={kpi.unit}
          anomalyData={anomalies}
        />
      </TimelineChartContainer>
    );
  }

  if (isLoading || isError || !kpiWithData) return <TimelineChartContainer />;

  // Apply client-side date filtering as a safeguard
  const filteredKpiData = {
    ...kpiWithData,
    entries: filterEntriesByDateRange(kpiWithData.entries ?? []),
  };

  return (
    <TimelineChartContainer>
      <ChartFromKpi
        chartType={chartType}
        title={kpi.name}
        description={getKpiUnitTranslationPlural(tKpiTypes, kpi.unit)}
        kpi={filteredKpiData}
        aggregationType={AggregationType.TIMELINE}
        chartClassName="h-[225px]"
        onUpdateChartType={updateChartType}
        availableChartTypes={timelineChartTypes}
        unit={kpi.unit}
        anomalyData={anomalies}
      />
    </TimelineChartContainer>
  );
};

const AccumulatedChart = ({
  kpi,
  projectId,
  isSubprojectFiltered,
  chartType,
  updateChartType,
  fromDate,
  toDate,
}: {
  kpi?: KpiWithMetrics;
  projectId: number | undefined;
  isSubprojectFiltered: boolean;
  chartType: ChartType;
  updateChartType: (chartType: ChartType) => void;
  fromDate?: Date;
  toDate?: Date;
}) => {
  if (!kpi) return <AccumulatedChartContainer />;

  const { t: tKpiTypes } = useTranslation('kpiTypes', { nsMode: 'fallback' });

  const { accumulatedChartTypes } = useInsightsHubChartsStore();

  const isAggregatedChartType = [
    ChartType.BATTERY,
    ChartType.BAR,
    ChartType.PIE,
  ].includes(chartType);

  // When date range is active, don't use unfiltered aggregation
  const hasDateFilter = !!(fromDate || toDate);
  const shouldAggregate =
    !isSubprojectFiltered &&
    isAggregatedChartType &&
    !!projectId &&
    !hasDateFilter;

  const projectIdForEntries = shouldAggregate ? undefined : projectId;

  const {
    data: aggregated,
    isLoading: isAggregatedLoading,
    isError: isAggregatedError,
  } = useQuery({
    queryKey: ['kpiAccumulatedLatestValue', projectId, kpi.id],
    queryFn: () =>
      getKpiAccumulatedLatestValue({ projectId: projectId!, kpiId: kpi.id }),
    enabled: shouldAggregate,
  });

  const {
    data: kpiWithData,
    isLoading,
    isError,
  } = useChartDataForKpi({
    id: kpi.id,
    projectId: projectIdForEntries,
    kpi,
    fromDate,
    toDate,
  });

  // Helper function to filter entries by date range (inclusive)
  const filterEntriesByDateRange = (
    entries: KpiTrackingEntry[],
  ): KpiTrackingEntry[] => {
    if (!fromDate && !toDate) return entries;

    return entries.filter((entry) => {
      if (!entry.created_at) return false;
      const entryDate = new Date(entry.created_at);

      // For fromDate: compare against start of day (00:00:00.000) - inclusive
      if (fromDate) {
        const fromDateStart = new Date(fromDate);
        fromDateStart.setHours(0, 0, 0, 0);
        if (entryDate < fromDateStart) return false;
      }

      // For toDate: compare against end of day (23:59:59.999) - inclusive
      if (toDate) {
        const toDateEnd = new Date(toDate);
        toDateEnd.setHours(23, 59, 59, 999);
        if (entryDate > toDateEnd) return false;
      }

      return true;
    });
  };

  if (shouldAggregate) {
    if (isAggregatedLoading || isAggregatedError || !aggregated)
      return <AccumulatedChartContainer />;

    const syntheticEntry: KpiTrackingEntry = {
      id: 0,
      kpi_id: kpi.id,
      project_id: projectId!,
      report_id: 0,
      value: aggregated.aggregatedValue,
      created_at: aggregated.lastEntryDate ?? new Date().toISOString(),
      updated_at: null,
      created_by: null,
      updated_by: null,
    };

    return (
      <AccumulatedChartContainer>
        <ChartFromKpi
          chartType={chartType}
          title={kpi.name}
          description={getKpiUnitTranslationPlural(tKpiTypes, kpi.unit)}
          kpi={{ ...kpi, entries: [syntheticEntry] }}
          aggregationType={AggregationType.ACCUMULATED}
          chartClassName="h-[225px]"
          onUpdateChartType={updateChartType}
          availableChartTypes={accumulatedChartTypes}
          unit={kpi.unit}
          chartSettings={{
            hideXAxis: true,
          }}
        />
      </AccumulatedChartContainer>
    );
  }

  if (isLoading || isError || !kpiWithData)
    return <AccumulatedChartContainer />;

  // Apply client-side date filtering as a safeguard
  const filteredEntries = filterEntriesByDateRange(kpiWithData.entries ?? []);
  const filteredKpiData = { ...kpiWithData, entries: filteredEntries };

  return (
    <AccumulatedChartContainer>
      <ChartFromKpi
        chartType={chartType}
        title={kpi.name}
        description={getKpiUnitTranslationPlural(tKpiTypes, kpi.unit)}
        kpi={filteredKpiData}
        aggregationType={AggregationType.ACCUMULATED}
        chartClassName="h-[225px]"
        onUpdateChartType={updateChartType}
        availableChartTypes={accumulatedChartTypes}
        unit={kpi.unit}
        chartSettings={{
          hideXAxis: true,
        }}
      />
    </AccumulatedChartContainer>
  );
};

const AccumulatedChartContainer = ({
  children,
}: {
  children?: React.ReactNode;
}) => {
  return (
    <div className="h-[398px] rounded-xl bg-dark-container">{children}</div>
  );
};
